import { useTranslation } from "react-i18next";
import { DataNodeModel } from "../pages/editor/diagram/nodes/DataNodeModel";
import { useGraph } from "./useGraph";

export type TopologyConfigurationError = {
  id: string;
  error: string;
};

export const useTopologyConfigurationValidator = () => {
  const { t } = useTranslation();
  const { engine } = useGraph();

  const validateTopologyConfiguration = (): TopologyConfigurationError[] => {
    return [
      ...validateNoOrphans(),
      ...validateNoEmptyKeyValues(),
      ...validateNoMissingRequirements(),
    ];
  };

  const checkIfYOutputArePresent = (): boolean => {
    var result = false
    engine
      ?.getModel()
      .getNodes()
      .forEach((node) => {
        const ports = Object.values((node as DataNodeModel).getPorts());
        const yPorts = ports.filter(
          (port) => Object.keys(port.getLinks()).length > 1
        );

        if (yPorts.length > 0) {
          result = true
        }
      });
      return result
  }

  const validateNoOrphans = (): TopologyConfigurationError[] => {
    const errors: TopologyConfigurationError[] = [];

    engine
      ?.getModel()
      .getNodes()
      .forEach((node) => {
        const ports = Object.values((node as DataNodeModel).getPorts());
        const unusedPorts = ports.filter(
          (port) => !Object.keys(port.getLinks()).length
        );

        unusedPorts.forEach((port) => {
          const error: TopologyConfigurationError = {
            id: `${(node as DataNodeModel).getName()}`,
            error: `Le port "${port.getName()}" de la brique n'est pas utilisé`,
          };
          errors.push(error);
        });
      });

    return errors;
  };

  const isEmptyKV = (obj: any): boolean =>
    obj &&
    typeof obj === 'object' &&
    'name' in obj &&
    'value' in obj &&
    obj.name === '' &&
    obj.value === '';

  const checkEmptySettings = (obj: any): boolean => {
      if (Array.isArray(obj)) {
        for (const item of obj) {
          if (isEmptyKV(item) || checkEmptySettings(item)) {
            return true;
          }
        }
      } else if (typeof obj === 'object' && obj !== null) {
        for (const value of Object.values(obj)) {
          if (checkEmptySettings(value)) {
            return true;
          }
        }
      }
      return false;
  };

  const validateNoEmptyKeyValues = (): TopologyConfigurationError[] => {
    const errors: TopologyConfigurationError[] = [];

    engine
      ?.getModel()
      .getNodes()
      .forEach((node) => {
        const dataNode = node as DataNodeModel;
        if(dataNode.isDisabled) {
          return
        }
        const settings = dataNode.schema?.settings;
        const values = dataNode.settings;

        if (!settings || !values) {
          return;
        }
        if (checkEmptySettings(values)) {
          const error: TopologyConfigurationError = {
            id: `${dataNode.getName()}`,
            error:  t("error.emptykeyvalue"),
          };
          errors.push(error);
        }
      });

    return errors;
  };


  const validateNoMissingRequirements = (): TopologyConfigurationError[] => {
    const errors: TopologyConfigurationError[] = [];

    engine
      ?.getModel()
      .getNodes()
      .forEach((node) => {
        const dataNode = node as DataNodeModel;
        if(dataNode.isDisabled) {
          return
        }
        const settings = dataNode.schema?.settings;
        const values = dataNode.settings;
        if (!settings || !values) {
          return;
        }
        for (const key in settings) {
          const setting = settings[key];
          const value = values[key];

          if(!!setting.condition) {
            const conditionValue = values[setting.condition.keyName] === undefined 
                ? settings[setting.condition.keyName].default_value 
                : values[setting.condition.keyName];
                let shouldContinue = true
                for(let value of setting.condition.values) {
                  if(value !== conditionValue) {
                    shouldContinue = false
                  } 
                }
                if(!shouldContinue)continue
          }

          if (!value && !setting.default_value && !setting.dependencies && setting.required && setting.type !== "boolean" && setting.type !== "booleanstring") {
            const error: TopologyConfigurationError = {
              id: `${dataNode.getName()}`,
              error: `Le champ requis "${setting.name}" n'a pas été complété`,
            };
            errors.push(error);
          }
        }
      });

    return errors;
  };

  

  return {
    validateTopologyConfiguration,
    checkIfYOutputArePresent,
  };
};